'use client';

// React
import { type ReactElement, useCallback } from 'react';

// Components
import QuantityPicker from '@/components/quantity-picker-custom/quantity-picker';
import { Label } from '@/components/ui/label';

// Utils
import { useTranslation } from 'react-i18next';

interface ProductQuantityPickerProps {
    /** String value displayed in the quantity input (controlled by the parent). */
    inputValue: string;
    /** Updates the input display value when the user changes quantity. */
    onInputValueChange: (value: string) => void;
    /** Syncs the parsed numeric quantity to product view / cart state. */
    onQuantityChange: (quantity: number) => void;
    /** Optional wrapper className. */
    className?: string;
    /** Product name passed through for quantity picker accessibility labels. */
    productName?: string;
    /** Disables increment, decrement, and direct input. */
    disabled?: boolean;
    /** Upper bound for selectable quantity (e.g. bonus products). */
    maxQuantity?: number;
}

/**
 * Controlled quantity picker for the PDP add-to-cart row.
 *
 * The parent owns both the input string state and cart quantity so inventory
 * warnings can be rendered outside the picker row at full layout width.
 */
export default function ProductQuantityPicker({
    inputValue,
    onInputValueChange,
    onQuantityChange,
    className,
    productName,
    disabled = false,
    maxQuantity,
}: ProductQuantityPickerProps): ReactElement {
    const { t: tQuantity } = useTranslation('quantitySelector');

    const handleQuantityChange = useCallback(
        (stringValue: string, numberValue: number) => {
            if (numberValue >= 0) {
                onQuantityChange(numberValue);
                onInputValueChange(stringValue);
            }
        },
        [onQuantityChange, onInputValueChange]
    );

    return (
        <div className={className}>
            <Label htmlFor="quantity" className="text-foreground sr-only">
                {tQuantity('quantity')}
            </Label>
            <QuantityPicker
                value={inputValue}
                min={1}
                max={maxQuantity}
                onChange={handleQuantityChange}
                productName={productName}
                disabled={disabled}
            />
        </div>
    );
}
